import ChartComponent from '../ChartComponent';
import { useNavigate, useParams } from 'react-router-dom';
import { ChartComponentDatasetType, DropdownOptionType, MemberDetailResponseType } from '../../types';
import { capitalizeFirstLetter } from '../../utils/texts';
import { useVoteColors } from '../../utils/hooks';
import Section from '../common/Section/Section';
import { useTranslation } from 'react-i18next';
import { URLKeys } from '../../Constants';

type Props = {
    memberData: MemberDetailResponseType;
    selectedParty: DropdownOptionType | null;
};

const MemberDetailVotingSummaryChartSection = ({ memberData, selectedParty }: Props) => {
    const { municipalitaId, bodyType, zastupitelstvoId } = useParams();
    const navigate = useNavigate();
    const voteColors: { [key: string]: string } = useVoteColors();
    const { t } = useTranslation();

    const voteOptions = memberData.pohlavi
        ? { ano: 1, ne: 2, 'zdržela se': 3, nehlasovala: 4, 'nepřít.': 5, omluvena: 6, tajná: 7 }
        : { ano: 1, ne: 2, 'zdržel se': 3, nehlasoval: 4, 'nepřít.': 5, omluven: 6, tajná: 7 };

    interface SummaryVotesLabelsCorrection {
        (arr: string[]): string[];
    }

    const summaryVotesLabelsCorrection: SummaryVotesLabelsCorrection = (arr) => {
        var idx = 0;
        if (arr.includes('zdržel se')) {
            idx = arr.indexOf('zdržel se');
            arr[idx] = 'zdržela se';
        }
        if (arr.includes('nehlasoval')) {
            idx = arr.indexOf('nehlasoval');
            arr[idx] = 'nehlasovala';
        }
        if (arr.includes('omluven')) {
            idx = arr.indexOf('omluven');
            arr[idx] = 'omluvena';
        }
        return arr;
    };

    const dataSource =
        selectedParty?.code === 0
            ? memberData?.hlasovani.sumarizace
            : memberData?.votes_per_party.find((item) => item.party_id === selectedParty?.code)?.vote_options || {};

    let summaryVotesLabels = memberData.pohlavi
        ? summaryVotesLabelsCorrection(Object.keys(dataSource))
        : Object.keys(dataSource);
    let summaryVotesValues = Object.values(dataSource);

    const datasetsDataSummaryVotes = Object.keys(dataSource).map((item, index) => ({
        type: 'bar',
        label: capitalizeFirstLetter(summaryVotesLabels[index]),
        backgroundColor: voteColors[item],
        data: [summaryVotesValues[index]]
    }));

    datasetsDataSummaryVotes.sort((a, b) => {
        return Object.keys(voteOptions).indexOf(a.label) - Object.keys(voteOptions).indexOf(b.label);
    });

    let summaryVotesData = {
        labels: [''],
        datasets: datasetsDataSummaryVotes
    };

    var hover_timeout: NodeJS.Timeout;

    const stackedOptionsVotes = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        aspectRatio: 0.8,
        plugins: {
            tooltips: {
                mode: 'index',
                intersect: false
            },
            legend: {
                labels: {
                    generateLabels: (chart: { data: { datasets: ChartComponentDatasetType[] } }) => {
                        const datasets = chart.data.datasets;
                        return datasets.map((data, i) => ({
                            text: `${data.label} (${data.data[0]})`,
                            fillStyle: data.backgroundColor,
                            strokeStyle: 'white',
                            datasetIndex: 0,
                            index: i
                        }));
                    }
                },
                onHover: function (
                    evt: { native: { target: { style: { cursor: string } } } },
                    item: { index: any },
                    legend: {
                        chart: { tooltip: any; data: { datasets: ChartComponentDatasetType[] }; update: () => void };
                    }
                ) {
                    const tooltip = legend.chart.tooltip;
                    if (tooltip.getActiveElements().length > 0) {
                        tooltip.setActiveElements([], { x: 0, y: 0 });
                    }
                    tooltip.setActiveElements([{ datasetIndex: item.index, index: 0 }], { x: 0, y: 0 });

                    evt.native.target.style.cursor = 'pointer';
                    legend.chart.data.datasets.forEach((i, id) => {
                        if (id !== item.index && i.backgroundColor.length === 7) {
                            i.backgroundColor += '30';
                        }
                    });
                    clearTimeout(hover_timeout);
                    hover_timeout = setTimeout(function () {
                        legend.chart.update();
                    }, 60);
                },
                onLeave: function (
                    evt: any,
                    item: { index: any },
                    legend: {
                        chart: { tooltip: any; data: { datasets: ChartComponentDatasetType[] }; update: () => void };
                    }
                ) {
                    const tooltip = legend.chart.tooltip;
                    tooltip.setActiveElements([], { x: 0, y: 0 });
                    legend.chart.data.datasets.forEach((i, id) => {
                        if (id !== item.index && i.backgroundColor.length > 7) {
                            i.backgroundColor = i.backgroundColor.slice(0, -2);
                        }
                    });
                    legend.chart.update();
                }
            }
        },
        scales: {
            x: {
                stacked: true
            },
            y: {
                stacked: true,
                position: 'right'
            }
        },
        onHover: function (evt: { native: { target: { style: { cursor: string } } } }, item: undefined[]) {
            if (item[0] !== undefined) {
                evt.native.target.style.cursor = 'pointer';
            } else {
                evt.native.target.style.cursor = 'default';
            }
        },
        onClick: function (evt: any, item: { datasetIndex: string | number }[]) {
            if (item[0] !== undefined) {
                let voteOpt = summaryVotesData.datasets[
                    item[0].datasetIndex as number
                ].label.toLowerCase() as keyof typeof voteOptions;

                // REDIRECT
                navigate(
                    `/${municipalitaId}/${bodyType}/${zastupitelstvoId}/hlasovani?${URLKeys.memberNumber}=${
                        memberData.id
                    }&${URLKeys.memberVote}=${[voteOptions[voteOpt]]}&${URLKeys.sortBy}=nejstarsi#voting-cards`
                );
            }
        }
    };

    return (
        <>
            <Section title={t('votings-summary')} subtitle={t('tooltip-chart')}>
                <ChartComponent
                    type='bar'
                    data={summaryVotesData}
                    options={stackedOptionsVotes}
                    height='200px'
                    width='100%'
                    xTitle={undefined}
                    yTitle={undefined}
                    aria={undefined}
                />
            </Section>
        </>
    );
};

export default MemberDetailVotingSummaryChartSection;
